import { Schema } from "effect"
import { Actor, EntityRef, Owned, Tier } from "../common"
import { GithubLogin, GitSha } from "../github"
import {
  AgentId,
  AreaId,
  CommentId,
  HookId,
  ItemId,
  KpiId,
  Millis,
  MonitorId,
  NotificationId,
  PosInt,
  ProjectId,
  ProposalId,
  RunId,
  UserId,
  WakeId,
  WorkRequestId,
  WorkspaceId,
} from "../ids"
import { ItemStatus, ReviewStage } from "./item"
import { MetricCondition } from "./plan"

// #region wakes
/** What a `changed` wake reports (decision 0048). */
export const ChangedWhat = Schema.Literals([
  "done",
  "cancelled",
  "sent_back",
  "edited",
  "decided",
  "goal_edited",
  "kpi",
  "approved", // Wave A: a person passed a review stage (amends decision 0048's silent rule)
  // decision 0059: the PR watch. `closed` = the PR was closed without merging (an item close stays done / cancelled)
  "merged",
  "closed",
  "changes_requested",
  "commented",
  "ci_failed",
  "conflicts",
  "new_commits",
])
export type ChangedWhat = typeof ChangedWhat.Type

/** `byPerson` (decision 0048): `true` when a person caused the wake, else absent (never `false`). */
const byPerson = Schema.optionalKey(Schema.Literal(true))
/** Wave A: how many agent-caused wakes in a row led here (≥ 1). Absent = 0: a person, a run, the system. */
const chain = Schema.optionalKey(PosInt)
/** Wave A: the person a decided card was sent to, when someone else decided it. */
const forUser = Schema.optionalKey(UserId)

/**
 * Why an agent wakes. Purely event-driven; time-based wakes only through explicit monitors and the overdue alarm.
 * Item reasons key their dedupe subject as `tsk:<itemId>` (core `wakeSubject`), so a wake derived before and after the
 * cutover still dedupes (decision 0036).
 */
export const WakeReason = Schema.TaggedUnion({
  project_work: {
    requestId: WorkRequestId,
    phase: Schema.Literals(["request", "reply"]),
    /** Additive for existing runner codecs: turn.md carries the scoped stop instruction. */
    control: Schema.optionalKey(Schema.Literal(true)),
    revision: Schema.optionalKey(PosInt),
    untrusted: Schema.Boolean,
    byPerson,
  },
  /** chat (owner: steer or new turn). `text`: the message's text (fix wave §4.7, for `.memory/turn.md`). */
  user_message: { messageId: Schema.String, from: UserId, text: Schema.optionalKey(Schema.String) },
  /** a PERSON's @mention or note (an agent's mention is `agent_mention`) */
  human_mention: { commentId: CommentId, from: UserId, untrusted: Schema.Boolean },
  /** Wave A: another agent @mentioned this agent in a comment (not an ask: that is `ask`). */
  agent_mention: { commentId: CommentId, from: AgentId, untrusted: Schema.Boolean, chain },
  assignment: { itemId: ItemId, byPerson, chain }, // it became the item's owner
  run_ended: { runId: RunId, itemId: Schema.NullOr(ItemId), outcome: Schema.String }, // a worker/validator on its item finished
  review_requested: { itemId: ItemId, round: Schema.Int }, // its "agent" review stage
  blocker: { itemId: ItemId, runId: Schema.NullOr(RunId), chain }, // runId null: a cancelled predecessor holds it (G5)
  impossible_claimed: { itemId: ItemId, runId: RunId, chain },
  ask: { askId: CommentId, chain }, // an ask addressed to it (from any agent or worker)
  answer: { askId: CommentId, byPerson, chain }, // an answer to its own ask
  /** One of its own proposals. `by`: the person who decided it; `for`: the card's recipient when not the decider. */
  proposal_resolved: {
    proposalId: ProposalId,
    state: Schema.String,
    byPerson,
    by: Schema.optionalKey(UserId),
    for: forUser,
    chain,
  },
  area_request: { itemId: ItemId, chain }, // a cross-area request for its area (it is the area's agent)
  /** Items entered triage (the lead; coalesced). Was `intake{intakeId}`. */
  triage: { itemId: ItemId, chain },
  /** A container's children are all closed (G1): close it, or plan the next phase. */
  children_closed: { itemId: ItemId },
  /** A person's item passed its due date while it holds others (G6): the nearest agent above. */
  overdue: { itemId: ItemId, dueAt: Millis, chain },
  /** Someone else changed the description while a run works on it. */
  spec_changed: { itemId: ItemId, runId: RunId, from: PosInt, to: PosInt, chain },
  kpi_threshold: { kpiId: KpiId }, // Phase 3
  /**
   * A person or another agent changed something the agent plans around, a person passed a review stage (`approved`),
   * or anyone but the agent recorded a KPI number (decision 0048, Wave A). `id` is an ItemId, GoalId or KpiId by
   * `subject`; `note` is one line (≤ 500 chars) built by core; `for`: the card's recipient when someone else decided it.
   */
  changed: {
    subject: Schema.Literals(["item", "goal", "kpi"]),
    id: Schema.String,
    what: ChangedWhat,
    by: Actor,
    note: Schema.optionalKey(Schema.String),
    for: forUser,
    chain,
    /**
     * Decision 0059: a PR fact. `viaMinga`: the person whose Merge in Minga merged it; `viaRun`: a Minga run merged it
     * with the person's gh. `status`/`stage`: the item's status and review stage after the commit.
     */
    pr: Schema.optionalKey(
      Schema.Struct({
        number: PosInt,
        url: Schema.String,
        login: Schema.optionalKey(GithubLogin),
        sha: Schema.optionalKey(GitSha),
        viaMinga: Schema.optionalKey(UserId),
        viaRun: Schema.optionalKey(Schema.Boolean),
        status: Schema.optionalKey(ItemStatus),
        stage: Schema.optionalKey(ReviewStage),
      }),
    ),
  },
  /** Wave C (decision 0071): a monitor fired. Change-started; asked by the monitor's setter (`Monitor.ownerId`). */
  monitor: { monitorId: MonitorId, kind: Schema.String },
  /**
   * Wave C (decision 0071), A1: the daily check found loose ends that are new since the last check, for subjects this
   * agent owns. `keys` as in `ProjectState.dailyCheck.keys` (`card:<proposalId>`, `pr:<artifactId>:<state>`,
   * `kpi:<kpiId>`, `ask:<commentId>`, `stuck:<itemId>`); `lines`: one plain line per key, built by core. Change-started;
   * asked by nobody (the keeper pays).
   */
  loose_ends: {
    keys: Schema.Array(Schema.String).check(Schema.isMaxLength(50)),
    lines: Schema.Array(Schema.String).check(Schema.isMaxLength(50)),
  },
  /**
   * Wave C (decision 0071): a recurring item (series) this agent owns is due for `period` (yyyy-mm-dd in the series'
   * zone) and has no open occurrence: press Run now. Change-started; asked by the series' Asked by person.
   */
  series_due: { itemId: ItemId, period: Schema.String },
})
export type WakeReason = typeof WakeReason.Type

/** One coalesced batch: ten finished items wake the agent once. It becomes the turn run's idempotency key. */
export const WakeBatch = Schema.Struct({
  wakeBatchId: WakeId,
  reasons: Schema.NonEmptyArray(WakeReason),
  coalescedFrom: Schema.Int,
  /** Wave A: the first batch after the agent was resumed; it holds what arrived while it was paused (since then). */
  pausedSince: Schema.optionalKey(Millis),
})
export type WakeBatch = typeof WakeBatch.Type
// #endregion wakes

// #region monitor
/** What fires a monitor. Webhooks and KPI thresholds are Phase 2/3 triggers for routines (decision 0002). */
export const MonitorTrigger = Schema.TaggedUnion({
  cron: { expr: Schema.String, tz: Schema.String },
  at: { at: Millis },
  webhook: { hookId: HookId },
  kpi_threshold: { condition: MetricCondition },
})

/**
 * Explicit wakes that are not caused by a graph event: heartbeat, digest, weekly review, curation, KPI check, bet
 * evaluation, and agent routines (decision 0002). Stored in the Project DO (`ProjectState.monitors`, wave C, decision
 * 0071: one clock in the Project DO, amending the "Agent DO owns its schedules" of decision 0002); `owner` is who it
 * belongs to (an agent, or an Area whose agent it wakes). `ownerId` is the person who set it (or accepted an agent's
 * `propose_monitor` card, or resumed it): its turns are asked by that person. A monitor only wakes its agent: the turn is
 * visible and may capture triage items or open proposals, never hidden work. Routines cannot create routines: a
 * SetMonitor{kind: routine} from a turn whose wake batch came from a routine is rejected. Wave C sets only
 * `trigger: cron` (no `at`, `webhook` or `kpi_threshold`); a setter who left or became a viewer is `state: blocked`.
 */
export const Monitor = Schema.Struct({
  id: MonitorId,
  ...Owned,
  agentId: AgentId, // the agent it wakes
  /** Actual creator, independent of the principal whose resources execute the monitor. Optional on old records. */
  createdBy: Schema.optionalKey(Actor),
  owner: Schema.TaggedUnion({ agent: { agentId: AgentId }, area: { projectId: ProjectId, areaId: AreaId } }),
  projectId: Schema.NullOr(ProjectId),
  kind: Schema.Literals([
    "heartbeat",
    "digest",
    "weekly_review",
    "curation",
    "kpi_check",
    "bet_eval",
    "routine",
  ]),
  trigger: MonitorTrigger,
  subject: Schema.NullOr(EntityRef),
  instruction: Schema.String, // what the wake is about, shown in the agent's inbox
  /** A fire while its previous turn is still active: skip it, or coalesce into the next batch. Missed fires
   *  (the DO was asleep past the time) are always skipped, never replayed. */
  ifActive: Schema.Literals(["skip", "coalesce"]),
  quietHours: Schema.NullOr(Schema.Struct({ from: Schema.String, to: Schema.String, tz: Schema.String })),
  /** failure → blocked: a routine whose turn failed stops firing until a person resumes it. */
  state: Schema.TaggedUnion({ active: {}, blocked: { reason: Schema.String, runId: Schema.NullOr(RunId) } }),
  /** Lineage: the routine whose turn created this monitor (never another routine). */
  createdByRoutine: Schema.NullOr(MonitorId),
  enabled: Schema.Boolean,
  /** Who paused it; automatic turns cannot undo a person's pause. */
  pausedBy: Schema.optionalKey(Schema.NullOr(Actor)),
  lastFiredAt: Schema.NullOr(Millis),
})
export type Monitor = typeof Monitor.Type
// #endregion monitor

// #region notification
/** Per recipient, in the recipient's User DO (fan-out on write). Needs-you = tier ∈ {blocking, soon}, not done. */
export const Notification = Schema.Struct({
  id: NotificationId,
  userId: UserId,
  workspaceId: WorkspaceId,
  projectId: Schema.NullOr(ProjectId),
  source: Schema.TaggedUnion({
    proposal: { proposalId: ProposalId },
    mention: { commentId: CommentId },
    item: { itemId: ItemId, event: Schema.String, ref: Schema.optionalKey(Schema.String) },
    work: { requestId: WorkRequestId },
    kpi: { kpiId: KpiId },
    runner: { runnerId: Schema.String, state: Schema.String },
    digest: { digestRef: Schema.String },
  }),
  tier: Tier,
  title: Schema.String,
  reason: Schema.String, // "why now"
  groupKey: Schema.String, // collapses bursts: "Mara finished 6 items"
  count: Schema.Int,
  /** Outward approvals are never actionable from the lock screen: the push only opens the preview. */
  actionableFromNotification: Schema.Boolean,
  createdAt: Millis,
  seenAt: Schema.NullOr(Millis),
  doneAt: Schema.NullOr(Millis), // acted on, or the source resolved elsewhere
  snoozedUntil: Schema.NullOr(Millis),
  push: Schema.Literals(["none", "pending", "sent", "batched", "quiet_hours"]),
  /** Decision 0144: the project's name, so the bell and push always name it. Absent on older rows. */
  projectName: Schema.optionalKey(Schema.String),
  /**
   * Decision 0144: a card's recommended answer when it may be sent from the notification (`actionableFromNotification`):
   * its label ("Approve") and key. The server re-checks it when the answer comes in.
   */
  answer: Schema.optionalKey(
    Schema.Struct({ key: Schema.String, label: Schema.String, revision: Schema.Int }),
  ),
})
export type Notification = typeof Notification.Type

export const NotificationInbox = Schema.Struct({
  notifications: Schema.Array(Notification),
  unread: Schema.Int,
  nextOffset: Schema.NullOr(Schema.Int),
})

export const NotificationPrefs = Schema.Struct({
  userId: UserId,
  quietHours: Schema.NullOr(Schema.Struct({ from: Schema.String, to: Schema.String, tz: Schema.String })),
  soonBatchMinutes: Schema.Int,
  /** Per-source overrides; outward approvals can't be demoted below "soon". */
  overrides: Schema.Array(Schema.Struct({ source: Schema.String, tier: Tier })),
  digestCron: Schema.NullOr(Schema.String),
})
// #endregion notification
